import { DateTime } from "luxon";
import type { PatchVisitOnlyBookingBody } from "../../../../hooks/Remote/Schedule/visit-only-patch-booking.api";
import type { LocationInventory } from "../../../FloorManager/typings/location-inventory";
import { toIsoString } from "../../../../components/FleetManagement/EventDetailDialog/functions/fleetEventSort.function";
import type {
  VisitorBookingEditDraft,
  VisitorBookingTableRow
} from "../visitor-bookings-table.partial";
import { isVisitorLocationDerivedFromPlaceOrRoom } from "../../../../components/Schedule/functions/visitor-schedule-field-editable.function";
import { parseVisitorNameFromExternal } from "./parseVisitorNameFromExternal.function";

function isoToLocalVisitDateAndTime(
  iso: string,
  timezone: string
): { date: string; time: string } | null {
  const dt = DateTime.fromISO(iso).setZone(timezone);
  if (!dt.isValid) return null;
  const date = dt.toISODate();
  if (!date) return null;
  return { date, time: dt.toFormat("HH:mm") };
}

export function resolveLocationInventoryIdForPatch(
  locations: LocationInventory[] | undefined,
  locationName: string | undefined
): number | undefined {
  const trimmed = locationName?.trim();
  if (!trimmed) return undefined;
  const match = (locations ?? []).find(loc => loc.name?.trim() === trimmed);
  const id = match?.id;
  return typeof id === "number" && Number.isFinite(id) ? id : undefined;
}

type BuildPatchContext = {
  row: VisitorBookingTableRow;
  draft: VisitorBookingEditDraft;
  locations: LocationInventory[] | undefined;
  timezone: string;
};

export function buildPatchVisitOnlyBookingPayload({
  row,
  draft,
  locations,
  timezone
}: BuildPatchContext): PatchVisitOnlyBookingBody | null {
  const body: PatchVisitOnlyBookingBody = {};
  const checkedIn = Boolean(row.checkedIn);

  const endIso = toIsoString(draft.end) ?? draft.end;
  if (endIso) {
    const endLocal = isoToLocalVisitDateAndTime(endIso, timezone);
    if (endLocal) {
      body.endDate = endLocal.date;
      body.endTime = endLocal.time;
    }
  }

  if (checkedIn) {
    const visitorInformation: PatchVisitOnlyBookingBody["visitorInformation"] = {};
    const email = draft.externalEmail?.trim();
    if (email != null && email !== row.externalEmail?.trim()) {
      visitorInformation.email = email;
    }
    const company = draft.externalCompany?.trim();
    if (company != null && company !== row.externalCompany?.trim()) {
      visitorInformation.company = company;
    }
    const contacts = draft.externalContacts?.trim();
    if (contacts != null && contacts !== row.externalContacts?.trim()) {
      visitorInformation.contacts = contacts;
    }
    if (Object.keys(visitorInformation).length > 0) {
      body.visitorInformation = visitorInformation;
    }
  } else {
    const startIso = toIsoString(draft.start) ?? draft.start;
    if (startIso) {
      const startLocal = isoToLocalVisitDateAndTime(startIso, timezone);
      if (startLocal) {
        body.startDate = startLocal.date;
        body.startTime = startLocal.time;
      }
    }

    const hostId = draft.userBookedBy?.trim();
    if (hostId && hostId !== row.userBookedBy?.trim()) {
      body.userBookedFor = hostId;
    }

    if (!isVisitorLocationDerivedFromPlaceOrRoom(row)) {
      const locationInventoryId = resolveLocationInventoryIdForPatch(locations, draft.locationName);
      const currentLocationId = row.bookingInventory?.locationInventory?.id;
      if (locationInventoryId != null && locationInventoryId !== currentLocationId) {
        body.locationInventoryId = locationInventoryId;
      }
    }

    const { firstName, surname } = parseVisitorNameFromExternal(draft.externalName);
    const visitorInformation: PatchVisitOnlyBookingBody["visitorInformation"] = {};
    if (firstName != null) visitorInformation.firstName = firstName;
    if (surname != null) visitorInformation.surname = surname;
    if (draft.externalEmail != null) visitorInformation.email = draft.externalEmail.trim();
    if (draft.externalCompany != null) visitorInformation.company = draft.externalCompany.trim();
    if (draft.externalContacts != null) visitorInformation.contacts = draft.externalContacts.trim();
    if (draft.externalVisitReason != null) {
      visitorInformation.visitReason = draft.externalVisitReason.trim();
    }
    if (Object.keys(visitorInformation).length > 0) {
      body.visitorInformation = visitorInformation;
    }
  }

  const hasChange =
    body.startDate != null ||
    body.endDate != null ||
    body.startTime != null ||
    body.endTime != null ||
    body.userBookedFor != null ||
    body.locationInventoryId != null ||
    body.visitorInformation != null;

  return hasChange ? body : null;
}
